import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Heart, Flame, Quote, Tag, Mail, Loader2 } from 'lucide-react';
import { Building2, Headset, ShieldCheck, Shield, Lock, Home, Key, Wifi, Coffee, Sun, Umbrella, Phone, CheckCircle, Award, CreditCard, Globe, Calendar, Users, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const ICON_MAP = {
  'building': Building2, 'headset': Headset, 'shield-check': ShieldCheck, 'shield': Shield,
  'lock': Lock, 'star': Star, 'heart': Heart, 'globe': Globe, 'map-pin': MapPin,
  'credit-card': CreditCard, 'users': Users, 'calendar': Calendar, 'home': Home,
  'key': Key, 'wifi': Wifi, 'coffee': Coffee, 'sun': Sun, 'umbrella': Umbrella,
  'phone': Phone, 'check-circle': CheckCircle, 'award': Award, 'banknote': Banknote,
};

function FeatureIcon({ name, color }) {
  const IconComponent = ICON_MAP[name] || Star;
  return <IconComponent className="w-8 h-8" style={{ color }} />;
}

const FLAG_MAP = {
  'PY': '\u{1F1F5}\u{1F1FE}', 'AR': '\u{1F1E6}\u{1F1F7}', 'BR': '\u{1F1E7}\u{1F1F7}',
  'UY': '\u{1F1FA}\u{1F1FE}', 'CL': '\u{1F1E8}\u{1F1F1}', 'CO': '\u{1F1E8}\u{1F1F4}',
  'MX': '\u{1F1F2}\u{1F1FD}', 'BO': '\u{1F1E7}\u{1F1F4}', 'PE': '\u{1F1F5}\u{1F1EA}',
  'EC': '\u{1F1EA}\u{1F1E8}',
};

const PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/35264654/pexels-photo-35264654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1572177215152-32f247303126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1765775635143-6462630748ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
];


export function FeaturesSection({ brandData, locale, bt, t, stats }) {
  const features = (locale === 'es' && brandData?.features?.length) ? brandData.features : [
    { icon: 'building', title: t('landing.why.properties', { count: stats.properties }), description: t('landing.why.properties.desc') },
    { icon: 'headset', title: t('landing.why.support'), description: t('landing.why.support.desc') },
    { icon: 'shield', title: t('landing.why.security'), description: t('landing.why.security.desc') },
    { icon: 'lock', title: t('landing.why.protection'), description: t('landing.why.protection.desc') },
  ];

  const iconColors = [
    { bg: 'var(--brand-accent, #DCE9E2)', fg: 'var(--brand-primary, #264639)' },
    { bg: '#F5E4DE', fg: 'var(--brand-secondary, #C36F4D)' },
    { bg: 'var(--brand-accent, #DCE9E2)', fg: 'var(--brand-primary, #264639)' },
    { bg: '#F5E4DE', fg: 'var(--brand-secondary, #C36F4D)' },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 animate-fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--brand-secondary, #C36F4D)', fontFamily: "'DM Sans', sans-serif" }}>
            {bt(brandData?.features_label, 'landing.why.label')}
          </p>
          <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-brand-primary" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
            {bt(brandData?.features_title, 'landing.why.title')}
          </h2>
        </div>
        <div className={`grid grid-cols-2 ${features.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-8`}>
          {features.map((feature, idx) => {
            const colorSet = iconColors[idx % iconColors.length];
            return (
              <div key={`feature-${feature.title}`} className="text-center p-8" data-testid={`feature-card-${idx}`}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: colorSet.bg }}>
                  <FeatureIcon name={feature.icon} color={colorSet.fg} />
                </div>
                <h3 className="text-xl font-semibold text-brand-primary mb-3" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>{feature.title}</h3>
                <p className="text-[var(--brand-text-secondary,#57534E)]">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


export function DestinationsSection({ brandData, locale, bt, t, countryStats = {} }) {
  const destinations = (locale === 'es' && brandData?.destinations) ? brandData.destinations : [
    { code: 'PY', name: 'Paraguay', count: 580, cities: 'Encarnacion, Asuncion, Ciudad del Este', color: '#DCE9E2' },
    { code: 'AR', name: 'Argentina', count: 75, cities: 'Posadas, Buenos Aires, Obera', color: '#F5E4DE' },
    { code: 'BR', name: 'Brasil', count: 10, cities: 'Foz do Iguacu', color: '#E5EDFF' },
  ];

  // Override counts with real DB data when available
  const enrichedDestinations = destinations.map(dest => ({
    ...dest,
    count: countryStats[dest.name] ?? dest.count
  }));

  return (
    <section className="py-16 bg-white dark:bg-[#1C1917]" data-testid="destinations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <p className="font-semibold uppercase tracking-[0.2em] text-xs mb-3" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
            {bt(brandData?.destinations_label, 'landing.destinations')}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-primary" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
            {bt(brandData?.destinations_title, 'landing.destinations.title')}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {enrichedDestinations.map((dest) => (
            <Link
              key={dest.name}
              to={`/properties?country=${dest.name}`}
              className="group relative overflow-hidden rounded-xl p-8 transition-all hover:shadow-lg hover:-translate-y-1 dark:border dark:border-[#44403C]"
              data-testid={`dest-card-${dest.code}`}
            >
              <div className="absolute inset-0 opacity-60 dark:opacity-20" style={{ backgroundColor: dest.color || '#DCE9E2' }} />
              <div className="absolute inset-0 hidden dark:block bg-[#292524]" style={{ zIndex: 0 }} />
              <div className="relative" style={{ zIndex: 1 }}>
                <span className="text-5xl mb-4 block">{FLAG_MAP[dest.code] || dest.code}</span>
                <h3 className="text-xl font-bold text-[#1C1917] dark:text-white mb-1" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>{dest.name}</h3>
                <p className="font-semibold text-lg mb-2 text-brand-accent">{dest.count} {t('landing.destinations.properties')}</p>
                <p className="text-sm text-[#57534E] dark:text-[#A8A29E]">{dest.cities}</p>
                <span className="inline-flex items-center mt-3 text-sm font-medium group-hover:gap-2 transition-all text-brand-accent">
                  {t('landing.destinations.explore')} <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


export function CouponBanner({ coupons, t }) {
  if (coupons.length === 0) return null;
  return (
    <section className="py-3" style={{ background: `linear-gradient(to right, var(--brand-primary, #264639), var(--brand-primary-hover, #1C352B))` }} data-testid="coupon-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 sm:gap-6 flex-wrap">
          {coupons.map((coupon) => (
            <div key={coupon.code} className="flex items-center gap-2 text-white" data-testid={`coupon-pill-${coupon.code}`}>
              <Tag className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: 'var(--brand-secondary, #C36F4D)' }} />
              <span className="text-xs sm:text-sm font-medium">
                {t('landing.coupon.use')} <span className="bg-white/20 px-2 py-0.5 rounded font-bold tracking-wider">{coupon.code}</span>
                {' '}{t('landing.coupon.get')}{' '}
                {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`} {t('landing.coupon.off')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


export function LastMinuteSection({ offers, t, formatPrice }) {
  if (offers.length === 0) return null;
  return (
    <section className="py-16 bg-gradient-to-b from-[#FFF5F0] to-[#F5F4F0]" data-testid="last-minute-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-[#C36F4D]" />
              <p className="font-semibold uppercase tracking-[0.2em] text-xs" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
                {t('landing.offers.label')}
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-brand-primary" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              {t('landing.offers.title')}
            </h2>
          </div>
          <Link to="/properties">
            <Button variant="outline" className="hidden sm:flex border-[#C36F4D] text-[#C36F4D] hover:bg-[#FFF5F0]">
              {t('landing.offers.viewAll')} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {offers.slice(0, 4).map((property, index) => (
            <Link key={property.property_id} to={`/properties/${property.property_id}`} className="group" data-testid={`offer-card-${property.property_id}`}>
              <div className="bg-white rounded-xl overflow-hidden shadow-sm card-hover h-full border-2 border-[#C36F4D]/20">
                <div className="relative h-44 overflow-hidden">
                  <img src={property.image_url || PROPERTY_IMAGES[index % PROPERTY_IMAGES.length]} alt={property.title} className="w-full h-full object-cover property-image" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-[#C36F4D] text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Flame className="w-3 h-3" />
                      {property.last_minute_label || t('landing.offers.label')}
                      {property.last_minute_discount && ` -${property.last_minute_discount}%`}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center text-[#78716C] text-sm mb-1">
                    <MapPin className="w-3.5 h-3.5 mr-1" />
                    <span className="truncate">{property.city}, {property.country}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#1C1917] line-clamp-2 mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                    {property.title}
                  </h3>
                  {property.price_min_usd && (
                    <div>
                      {property.price_max_usd && property.price_max_usd > property.price_min_usd && (
                        <span className="text-sm text-[#78716C] line-through mr-2">{formatPrice(property.price_max_usd)}</span>
                      )}
                      <span className="font-bold text-[#C36F4D]">{formatPrice(property.price_min_usd)}</span>
                      <span className="text-xs text-[#78716C]"> {t('common.perNight')}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


export function TestimonialsSection() {
  // Hidden until real verified guest reviews exist
  return null;
}


export function CtaSection({ brandData, bt }) {
  return (
    <section className="py-20" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          {bt(brandData?.cta_title, 'landing.cta.title')}
        </h2>
        <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
          {bt(brandData?.cta_subtitle, 'landing.cta.subtitle')}
        </p>
        <a href="/sumar-alojamiento">
          <Button className="text-white px-8 py-3 text-lg" style={{ backgroundColor: 'var(--brand-secondary, #C36F4D)' }} data-testid="cta-register-btn">
            {bt(brandData?.cta_button, 'landing.cta.button')}
          </Button>
        </a>
      </div>
    </section>
  );
}


export function NewsletterSection({ t, api }) {
  const [nlEmail, setNlEmail] = useState('');
  const [nlSending, setNlSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlSending(true);
    try {
      const res = await api.post('/newsletter/subscribe', { email: nlEmail });
      if (res.data.already_subscribed) {
        toast.info(t('landing.newsletter.already'));
      } else {
        toast.success(t('landing.newsletter.success'));
      }
      setNlEmail('');
    } catch (err) {
      toast.error(t('landing.newsletter.error'));
    } finally {
      setNlSending(false);
    }
  };

  return (
    <section className="py-14 bg-[#264639]" data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Mail className="w-8 h-8 text-white/60 mx-auto mb-3" />
        <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
          {t('landing.newsletter.title')}
        </h2>
        <p className="text-white/70 text-sm mb-6">{t('landing.newsletter.desc')}</p>
        <form onSubmit={handleSubmit} className="flex gap-3 max-w-md mx-auto">
          <Input
            type="email" value={nlEmail} onChange={(e) => setNlEmail(e.target.value)}
            placeholder={t('landing.newsletter.placeholder')}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 flex-1"
            required data-testid="newsletter-email-input"
          />
          <Button type="submit" disabled={nlSending} className="bg-[#C36F4D] hover:bg-[#A85B3D] px-6" data-testid="newsletter-subscribe-btn">
            {nlSending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('landing.newsletter.subscribe')}
          </Button>
        </form>
      </div>
    </section>
  );
}
